import { Avatar, Box, Chip, Stack, Typography } from "@mui/material";
import type { JSX } from "react";
import { globalFormatCurrencyBRL, globalTranslateCapitalizationStatus, globalBackgroundCapitalization, globalColorTextCapitalization, globalTranslateMaritalStatusCapitalization } from "../../../../shared/utils";

/* ---------------------------- STATUS CHIP ---------------------------- */
export function StatusChip({ status }: { status?: string }) {
    const label = globalTranslateCapitalizationStatus(status || "");
    const bg = globalBackgroundCapitalization(status || "");
    const color = globalColorTextCapitalization(status || "");

    return (
        <Chip
            label={
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box
                        sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: color,
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: "1rem",
                            fontWeight: 600,
                            letterSpacing: 0.3
                        }}
                    >
                        {label}
                    </Typography>
                </Stack>
            }
            sx={{
                border: `1px solid ${color}`,
                color: color,
                backgroundColor: bg, // Using the background from utils, or we could use a transparent version
                backdropFilter: "blur(4px)",
                px: 1.4,
                py: 0.2,
                height: 26,
                borderRadius: 12,
                display: "flex",
                alignItems: "center",
            }}
        />
    );
}

/* ---------------------------- INFO ROW ---------------------------- */
export function InfoRow({
    icon,
    label,
    value,
}: {
    icon: JSX.Element;
    label: string;
    value?: string | JSX.Element | null;
}) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Avatar
                variant="rounded"
                sx={{
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                    width: 34,
                    height: 34,
                    fontSize: 18,
                    boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}`,
                }}
            >
                {icon}
            </Avatar>

            <Stack spacing={0.3} flex={1}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                    {label}
                </Typography>

                <Typography variant="body2" fontWeight={500}>
                    {value || "—"}
                </Typography>
            </Stack>
        </Stack>
    );
}

/* ---------------------- DATA PREPARATION LOGIC ---------------------- */
export function prepareCapitalizationData(data: any) {
    // formatadores
    const fmt = (n?: number | string) =>
        globalFormatCurrencyBRL(String(Number(n ?? 0)));

    const fdate = (d?: string) =>
        d ? new Date(d).toLocaleDateString() : "—";

    // nomes
    const insurerName = data?.insuranceCompany ?? "Seguradora não informada";
    const realEstateName = data?.clientName || data?.realEstateId || "Imobiliária não informada";

    // valores principais
    const titleAmount = Number(data?.titleAmount ?? 0);
    const plan = data?.plan ? `${data.plan} meses` : "—";

    // vigência
    const startDate = data?.startDate;
    const endDate = data?.endDate;

    // endereço completo
    const addressLine = [
        [data?.propertyStreet, data?.propertyNumber, data?.propertyComplement]
            .filter(Boolean)
            .join(", "),
        [
            data?.propertyDistrict,
            `${data?.propertyCity} - ${data?.propertyState}`,
            data?.propertyZipcode ? `CEP: ${data.propertyZipcode}` : null,
        ]
            .filter(Boolean)
            .join(" | "),
    ]
        .filter(Boolean)
        .join(" • ");

    return {
        fmt,
        fdate,
        insurerName,
        realEstateName,
        titleAmount,
        plan,
        startDate,
        endDate,
        addressLine,
    };
}
